import React, {useRef, useState} from 'react';
import NavBar from "./components/NavBar";
import Header from "./components/Header";
import axios from "axios";
import SuccessPopUp from "./helpers/SuccessPopUp";
import FailPopUp from "./helpers/FailPopUp";
import {useNavigate} from "react-router-dom";
import ToolTip from './components/ToolTip';

interface ProductState {
    title_lv: string;
    title_eng: string;
    title_ru: string;
    description_lv: string;
    description_eng: string;
    description_ru: string;
    size: string;
    price: string;
    main_image: File | null
    gallery_images: (File | null)[];
    is_sold: boolean
}

interface ApiErrors {
    [key: string]: string[];
}

const AddProduct: React.FC = () => {

    const [product, setProduct] = useState<ProductState>({
        title_lv: '',
        title_eng: '',
        title_ru: '',
        description_lv: '',
        description_eng: '',
        description_ru: '',
        size: '',
        price: '',
        main_image: null,
        gallery_images: Array(6).fill(null),
        is_sold: false
    });

    const [successPopUp, setSuccessPopUp] = useState<Boolean>(false)
    const [failPopUp, setFailPopUp] = useState<Boolean>(false)

    const [message, setMessage] = useState<String>('')

    const [errors, setErrors] = useState<ApiErrors>({})

    const [loader, setLoader] = useState<Boolean>(false)

    const API_URL = `${process.env.REACT_APP_API_URL}`

    const [isNavBarMinimized, setIsNavBarMinimized] = useState(false);

    const navigate = useNavigate()

    const toggleSidebar = () => {
        setIsNavBarMinimized(!isNavBarMinimized);

    };

    const mainImageInputRef = useRef<HTMLInputElement>(null);
    const galleryImageInputRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

    //Handle Input data reading
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type} = e.target;

        if(type === 'checkbox' && 'checked' in e.target)
        {
            setProduct((prevState) => ({
                ...prevState,
                [name]: (e.target as HTMLInputElement).checked,
            }));
        }else
        {
            setProduct((prevState) => ({
                ...prevState,
                [name]:value,
            }));
        }
    };

    //Handle Main Image
    const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setProduct((prevState) => ({
                ...prevState,
                main_image: e.target.files![0],
            }));
        }
    };

    //Clear the Main Image Using the (X) button
    const clearMainImage = () => {
        setProduct((prevState) => ({
            ...prevState,
            main_image: null,
        }));
        if (mainImageInputRef.current) {
            mainImageInputRef.current.value = '';
        }
    };

    //Handle gallery images by indexes
    const handleGalleryImageChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const updatedGalleryImages = [...product.gallery_images];
            updatedGalleryImages[index] = e.target.files[0];
            setProduct((prevState) => ({
                ...prevState,
                gallery_images: updatedGalleryImages,
            }));
        }
    };

    //Handle gallery clear by indexes using the (X) button
    const clearGalleryImage = (index: number) => {
        const updatedGalleryImages = [...product.gallery_images];
        updatedGalleryImages[index] = null;
        setProduct((prevState) => ({
            ...prevState,
            gallery_images: updatedGalleryImages,
        }));
        if (galleryImageInputRefs.current[index]) {
            galleryImageInputRefs.current[index]!.value = '';
        }
    };

    //Send the collected data to backend via API
    const addProduct = async () => {

        setLoader(true)

        const payLoad = new FormData()

        //Add retrieved variables to the payload
        payLoad.append('product_title_lv', product.title_lv)
        payLoad.append('product_title_eng', product.title_eng)
        payLoad.append('product_title_ru', product.title_ru)

        payLoad.append('product_desc_lv', product.description_lv)
        payLoad.append('product_desc_eng', product.description_eng)
        payLoad.append('product_desc_ru', product.description_ru)

        payLoad.append('price', product.price)

        payLoad.append('dimensions', product.size)

        let is_sold_true = 0

        if(product.is_sold)
        {
            is_sold_true = 1
        }

        payLoad.append('is_sold', String(is_sold_true))

        if (product.main_image)
        {
            payLoad.append('main_image', product.main_image);
        }

        product.gallery_images.forEach((image, index) => {
            if (image)
            {
                payLoad.append(`images[]`, image);
            }
        });

        await axios.post(`${API_URL}/create-product`, payLoad, {withCredentials: true})
            .then(response => {
                setLoader(false)
                if(response.status === 201)
                {
                    navigate('/products',{
                        state: {
                            addMessage: response.data.message,
                            addSuccessPopUp: true,
                        },
                    })
                }
            }).catch(error => {
                if(error.status === 422)
                {
                    setErrors(error.response.data.errors)
                    setLoader(false)
                }
                if(error.status === 404)
                {
                    setMessage(error.message)
                    setFailPopUp(true)
                }
            })
    }


    return (
        <>
            {successPopUp && <SuccessPopUp message={message} setSuccessPopUp={setSuccessPopUp}/>}
            {failPopUp && <FailPopUp message={message} setFailPopUp={setFailPopUp}/>}

            <div className="h-screen">
                <NavBar isNavbarMinimized={isNavBarMinimized}/>
                <div className={`flex flex-col md:ml-[290px] h-screen overflow-y-scroll ml-0 transition-all duration-300 ease-in-out`}>
                    <Header toggleSidebar={toggleSidebar} isNavBarMinimized={isNavBarMinimized}/>
                    <div className="bg-neutral-100 flex flex-col flex-grow p-4 lg:p-6">
                        <div className="pb-4 flex items-center justify-between">
                            <h1 className="text-xl font-medium text-gray-800">Add New Product</h1>
                            <button disabled={loader ? true : false} onClick={addProduct} className={`${loader && '!bg-blue-200'} admin-btn`}>
                                {loader ?
                                    <div className="w-5 h-5 border-4 border-t-blue-500 border-white rounded-full animate-spin"></div>
                                    :
                                    'Add Product'
                                }
                            </button>
                        </div>

                        <div className="flex flex-col lg:flex-row flex-grow lg:space-x-4 space-y-4 lg:space-y-0">
                            <div className="flex flex-col bg-white border border-gray-200 rounded-2xl w-full lg:w-2/3">
                                <div className="p-6 border-b border-gray-200">
                                    <h1 className="font-medium text-gray-700">Product Information</h1>
                                </div>
                                <div className="flex-grow flex flex-col p-4 space-y-2">
                                    <div>
                                        <div className="flex space-x-2">
                                            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="product-title">Product Title (LV)</label>
                                            <span className="text-red-500 text-lg">*</span>
                                        </div>
                                            <input
                                                onChange={handleInputChange}
                                                value={product.title_lv}
                                                name="title_lv"
                                                id="product-title"
                                                type="text"
                                                className={`${errors.product_title_lv ? 'border-red-500' : 'border-gray-300'} h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm focus:outline-none bg-transparent text-gray-800`}
                                            />
                                        <span className="text-red-500">{errors.product_title_lv}</span>
                                    </div>
                                    <div>
                                        <div className="flex space-x-2">
                                            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="product-title">Product Title (ENG)</label>
                                            <span className="text-red-500 text-lg">*</span>
                                        </div>
                                        <input
                                            onChange={handleInputChange}
                                            value={product.title_eng}
                                            name="title_eng"
                                            id="product-title"
                                            type="text"
                                            className={`${errors.product_title_eng ? 'border-red-500' : 'border-gray-300'} h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm focus:outline-none bg-transparent text-gray-800`}
                                        />
                                    </div>
                                    <span className="text-red-500">{errors.product_title_eng}</span>

                                    <div>
                                        <div className="flex space-x-2">
                                            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="product-title">Product Title (RU)</label>
                                            <span className="text-red-500 text-lg">*</span>
                                        </div>
                                        <input
                                            onChange={handleInputChange}
                                            value={product.title_ru}
                                            name="title_ru"
                                            id="product-title"
                                            type="text"
                                            className={`${errors.product_title_ru ? 'border-red-500' : 'border-gray-300'} h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm focus:outline-none bg-transparent text-gray-800`}
                                        />
                                        <span className="text-red-500">{errors.product_title_ru}</span>

                                    </div>
                                    <div className="flex-grow flex flex-col">
                                        <div className="flex space-x-2">
                                            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="product-desc">Product Description (LV)</label>
                                            <span className="text-red-500 text-lg">*</span>
                                        </div>
                                        <textarea
                                            onChange={handleInputChange}
                                            value={product.description_lv}
                                            name="description_lv"
                                            id="product-desc"
                                            className={`${errors.product_desc_lv ? 'border-red-500' : 'border-gray-300'} w-full h-48 resize-none flex-grow rounded-lg border appearance-none px-4 py-2.5 text-sm focus:outline-none bg-transparent text-gray-800`}
                                        />
                                        <span className="text-red-500">{errors.product_desc_lv}</span>

                                    </div>
                                    <div className="flex-grow flex flex-col">
                                        <div className="flex space-x-2">
                                            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="product-desc">Product Description (ENG)</label>
                                            <span className="text-red-500 text-lg">*</span>
                                        </div>
                                        <textarea
                                            onChange={handleInputChange}
                                            value={product.description_eng}
                                            name="description_eng"
                                            id="product-desc"
                                            className={`${errors.product_desc_eng ? 'border-red-500' : 'border-gray-300'} w-full h-48 resize-none flex-grow rounded-lg border appearance-none px-4 py-2.5 text-sm focus:outline-none bg-transparent text-gray-800`}
                                        />
                                        <span className="text-red-500">{errors.product_desc_eng}</span>
                                    </div>
                                    <div className="flex-grow flex flex-col">
                                        <div className="flex space-x-2">
                                            <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="product-desc">Product Description (RU)</label>
                                            <span className="text-red-500 text-lg">*</span>
                                        </div>
                                        <textarea
                                            onChange={handleInputChange}
                                            value={product.description_ru}
                                            name="description_ru"
                                            id="product-desc"
                                            className={`${errors.product_desc_ru ? 'border-red-500' : 'border-gray-300'} w-full h-48 resize-none flex-grow rounded-lg border appearance-none px-4 py-2.5 text-sm focus:outline-none bg-transparent text-gray-800`}
                                        />
                                        <span className="text-red-500">{errors.product_desc_ru}</span>
                                    </div>
                                    <div>
                                        <div className="flex md:flex-row flex-col space-x-2">
                                            <div className="w-full lg:w-1/2">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex space-x-2">
                                                        <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="product-size">Product Size (cm)</label>
                                                        <span className="text-red-500 text-lg">*</span>
                                                    </div>
                                                    <div>
                                                        <ToolTip text={'Size format example: 10 W x 15 H x 0.5 D'}>
                                                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                                                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                                                <g id="SVGRepo_iconCarrier">
                                                                    <path d="M12 17V11" stroke="#4b5563" stroke-width="1.5" stroke-linecap="round"></path>
                                                                    <circle cx="1" cy="1" r="1" transform="matrix(1 0 0 -1 11 9)" fill="#4b5563"></circle>
                                                                    <path d="M7 3.33782C8.47087 2.48697 10.1786 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 10.1786 2.48697 8.47087 3.33782 7" stroke="#4b5563" stroke-width="1.5" stroke-linecap="round"></path>
                                                                </g>
                                                            </svg>
                                                        </ToolTip>
                                                    </div>
                                                </div>
                                                <input
                                                    onChange={handleInputChange}
                                                    value={product.size}
                                                    name="size"
                                                    id="product-size"
                                                    type="text"
                                                    className={`${errors.dimensions ? 'border-red-500' : 'border-gray-300'} h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm focus:outline-none bg-transparent text-gray-800`}
                                                />
                                                <span className="text-red-500">{errors.dimensions}</span>
                                            </div>

                                            <div className="w-full lg:w-1/2">
                                                <div className="flex space-x-2">
                                                    <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="product-price">Product Price (€)</label>
                                                    <span className="text-red-500 text-lg">*</span>
                                                </div>
                                                <input
                                                    onChange={handleInputChange}
                                                    value={product.price}
                                                    name="price"
                                                    id="product-price"
                                                    type="text"
                                                    className={`${errors.price ? 'border-red-500' : 'border-gray-300'} h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm focus:outline-none bg-transparent text-gray-800`}
                                                />
                                                <span className="text-red-500">{errors.price}</span>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="w-full lg:w-1/3">
                                <div className="flex flex-col bg-white border border-gray-200 rounded-2xl w-full h-[35rem]">
                                    <div className="p-6 border-b border-gray-200">
                                        <h1 className="font-medium text-gray-700">Upload Image</h1>
                                    </div>
                                    <div className="flex-grow flex flex-col">
                                        <div className="flex flex-col  justify-center w-full mt-4 px-4 h-1/2">
                                            {product.main_image ? (
                                                    <div className="relative flex flex-col items-center justify-center w-full h-full border border-gray-200 rounded-lg p-2">
                                                        <img
                                                            src={URL.createObjectURL(product.main_image)}
                                                            alt="Main"
                                                            className="w-full h-52 object-contain"
                                                        />
                                                        <input
                                                            id="main-image"
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={handleMainImageChange}
                                                            className="hidden"
                                                        />
                                                        <button
                                                            onClick={clearMainImage}
                                                            className="absolute flex items-center justify-center z-40 top-1 right-1 bg-white p-1 w-8 h-8 rounded-full border border-gray-300">
                                                            <svg className="h-6 fill-gray-400" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                                                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                                                <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                                                <g id="SVGRepo_iconCarrier">
                                                                    <path d="M18.8,16l5.5-5.5c0.8-0.8,0.8-2,0-2.8l0,0C24,7.3,23.5,7,23,7c-0.5,0-1,0.2-1.4,0.6L16,13.2l-5.5-5.5 c-0.8-0.8-2.1-0.8-2.8,0C7.3,8,7,8.5,7,9.1s0.2,1,0.6,1.4l5.5,5.5l-5.5,5.5C7.3,21.9,7,22.4,7,23c0,0.5,0.2,1,0.6,1.4 C8,24.8,8.5,25,9,25c0.5,0,1-0.2,1.4-0.6l5.5-5.5l5.5,5.5c0.8,0.8,2.1,0.8,2.8,0c0.8-0.8,0.8-2.1,0-2.8L18.8,16z"></path>
                                                                </g>
                                                            </svg>
                                                        </button>
                                                    </div>
                                            ): (
                                            <label htmlFor="main-image"
                                                   className={`${errors.main_image ? 'border-red-500' : 'border-gray-300'} flex flex-col items-center justify-center w-full h-full border rounded-lg cursor-pointer`}>
                                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                    <svg className="h-12 w-12 stroke-gray-500 mb-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                                        <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                                        <g id="SVGRepo_iconCarrier">
                                                            <path d="M15 12L12 12M12 12L9 12M12 12L12 9M12 12L12 15" strokeWidth="1.5" strokeLinecap="round"></path>
                                                            <path d="M7 3.33782C8.47087 2.48697 10.1786 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 10.1786 2.48697 8.47087 3.33782 7" strokeWidth="1.5" strokeLinecap="round"></path>
                                                        </g>
                                                    </svg>
                                                    <p className="mb-2 text-sm text-gray-500 text-center"><span
                                                        className="font-semibold">Click to upload product images</span></p>
                                                    <p className="text-xs text-gray-500 text-center">JPEG, PNG & JPG</p>
                                                </div>
                                                <input
                                                    id="main-image"
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleMainImageChange}
                                                    className="hidden"
                                                />
                                            </label>
                                            )}
                                            <span className="text-red-500">{errors.main_image}</span>
                                        </div>
                                        <div className="grid grid-cols-3 grid-rows-2 gap-2 p-4 flex-grow">
                                            {product.gallery_images.map((image, index) => (
                                                <div key={index} className="flex flex-grow">
                                                    {image ? (
                                                        <div className="relative flex flex-col items-center justify-center w-full h-full border border-gray-300 rounded-lg p-2">
                                                            <img
                                                                src={URL.createObjectURL(image)}
                                                                alt={`Gallery ${index}`}
                                                                className="w-full h-[4.5rem] object-contain"
                                                            />
                                                            <button
                                                                onClick={() => clearGalleryImage(index)}
                                                                className="absolute flex items-center justify-center z-40 top-1 right-1 bg-white p-1 w-8 h-8 rounded-full border border-gray-200"
                                                            >
                                                                <svg className="h-6 fill-gray-400" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                                                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                                                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                                                    <g id="SVGRepo_iconCarrier">
                                                                        <path d="M18.8,16l5.5-5.5c0.8-0.8,0.8-2,0-2.8l0,0C24,7.3,23.5,7,23,7c-0.5,0-1,0.2-1.4,0.6L16,13.2l-5.5-5.5 c-0.8-0.8-2.1-0.8-2.8,0C7.3,8,7,8.5,7,9.1s0.2,1,0.6,1.4l5.5,5.5l-5.5,5.5C7.3,21.9,7,22.4,7,23c0,0.5,0.2,1,0.6,1.4 C8,24.8,8.5,25,9,25c0.5,0,1-0.2,1.4-0.6l5.5-5.5l5.5,5.5c0.8,0.8,2.1,0.8,2.8,0c0.8-0.8,0.8-2.1,0-2.8L18.8,16z"></path>
                                                                    </g>
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <label
                                                            htmlFor={`gallery-image-${index}`}
                                                            className="flex flex-col items-center justify-center w-full h-full border border-gray-300 rounded-lg cursor-pointer"
                                                        >
                                                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                                <svg className="h-7 w-7 stroke-gray-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                                                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                                                    <g id="SVGRepo_iconCarrier">
                                                                        <path d="M15 12L12 12M12 12L9 12M12 12L12 9M12 12L12 15" strokeWidth="1.5" strokeLinecap="round"></path>
                                                                        <path d="M7 3.33782C8.47087 2.48697 10.1786 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 10.1786 2.48697 8.47087 3.33782 7" strokeWidth="1.5" strokeLinecap="round"></path>
                                                                    </g>
                                                                </svg>
                                                            </div>
                                                            <input
                                                                id={`gallery-image-${index}`}
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={handleGalleryImageChange(index)}
                                                                className="hidden"
                                                            />
                                                        </label>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white border border-gray-200 rounded-2xl mt-4">
                                    <h1 className="text-gray-700 font-medium border-b border-gray-200 p-4">Sold</h1>
                                    <div className="flex justify-between items-center p-4">
                                        <p className="text-gray-500 text-sm">Change the product to already sold.</p>
                                        <input
                                            onChange={handleInputChange}
                                            name='is_sold'
                                            checked={product.is_sold}
                                            className="appearance-none w-5 h-5 border border-gray-400 rounded-sm bg-white checked:bg-blue-400 checked:border-blue-400 cursor-pointer"
                                            type="checkbox"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddProduct;